import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { allVideos, articles } from '@/mocks/data';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [contentType, setContentType] = useState<'video' | 'article' | null>(null);

  const video = allVideos.find((v) => v.id === Number(id));
  const article = articles.find((a) => a.id === Number(id));

  const currentItem = video || article;

  useEffect(() => {
    if (video) setContentType('video');
    else if (article) setContentType('article');
    else setContentType(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, video, article]);

  const relatedVideos = video
    ? allVideos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4)
    : [];

  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!currentItem || !contentType) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-800 font-heading mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该内容可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: currentItem.title,
    description: 'summary' in currentItem ? currentItem.summary : currentItem.description,
    image: currentItem.thumbnail,
    author: {
      '@type': 'Organization',
      name: '69视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '69视频',
    },
    datePublished: 'date' in currentItem ? currentItem.date : '2026-01-01',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.69shipin.com/article/${currentItem.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${currentItem.title} - 69视频`}
        description={'summary' in currentItem ? currentItem.summary : currentItem.description}
        keywords={`${currentItem.title},69视频,${currentItem.categoryName},免费在线观看`}
        canonicalUrl={`https://www.69shipin.com/article/${currentItem.id}`}
        ogType="article"
        structuredData={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentItem.categoryName, href: `/category/${currentItem.category}` },
            { label: currentItem.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[900px] mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${currentItem.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {currentItem.categoryName}
                </Link>
                {'date' in currentItem && (
                  <span className="text-xs text-foreground-500">{currentItem.date}</span>
                )}
                {'year' in currentItem && (
                  <span className="text-xs text-foreground-500">{currentItem.year}</span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight">
                {currentItem.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                {'views' in currentItem && (
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {currentItem.views}{'duration' in currentItem ? '次播放' : '阅读'}
                  </span>
                )}
                {'author' in currentItem && (
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {currentItem.author}
                  </span>
                )}
                {'rating' in currentItem && (
                  <span className="flex items-center gap-1 text-accent-600 font-semibold">
                    <i className="ri-star-fill"></i>
                    {currentItem.rating}
                  </span>
                )}
              </div>
            </header>

            <div className="aspect-video rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src={currentItem.thumbnail}
                alt={currentItem.title}
                title={`${currentItem.title} - 69视频`}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-sm max-w-none">
              {'description' in currentItem && contentType === 'video' && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-foreground-800 font-heading mb-3">简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {currentItem.description}
                  </p>
                  <div className="flex items-center gap-4 mt-4 p-4 bg-background-100 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500">时长：</span>
                      <span className="text-sm font-medium text-foreground-800">{currentItem.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500">评分：</span>
                      <span className="text-sm font-medium text-accent-600">{currentItem.rating}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500">年份：</span>
                      <span className="text-sm font-medium text-foreground-800">{currentItem.year}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                    {currentItem.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-800 text-xs rounded whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {'content' in currentItem && contentType === 'article' && (
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                  {currentItem.content.split('\n\n').filter(Boolean).map((paragraph, i) => (
                    <p key={i}>{paragraph.trim()}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedVideos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/article/${v.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        title={`${v.title} - 69视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded">
                        {v.duration}
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded whitespace-nowrap">
                          {v.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="mt-2.5 text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1 font-heading">
                      {v.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                      <span>{v.categoryName}</span>
                      <span>·</span>
                      <span>{v.views}次播放</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关资讯
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.id}`}
                    className="group block bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={a.thumbnail}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                        {a.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                        <span><i className="ri-eye-line mr-1"></i>{a.views}</span>
                        <span>{a.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}